// Form values → login request DTO.
export const buildLoginPayload = ({ email, password, otp }) => {
  const dto = { email: email.trim().toLowerCase(), password };
  if (otp) dto.otp = otp;
  return dto;
};

export const processLoginResult = (dto) => {
  if (dto.user) {
    return { authenticated: true, user: processUser(dto.user) };
  }
  return { authenticated: false, message: dto.message };
};

const OAUTH_TYPE_BY_PROVIDER = { google: 'GOOGLE', microsoft: 'MICROSOFT' };

export const buildOauthRedirectParams = (provider, urlType = 'LOGIN') => {
  return { oauth_type: OAUTH_TYPE_BY_PROVIDER[provider], url_type: urlType };
};

export const processOauthRedirect = (dto) => {
  return { redirectUrl: dto.output?.redirect_url, state: dto.output?.state };
};

export const buildOauthLoginParams = (provider) => {
  return { oauth_type: OAUTH_TYPE_BY_PROVIDER[provider] };
};

const processTerminal = (terminal) => ({
  id: String(terminal.id),
  name: terminal.terminal_name ?? '',
  code: terminal.terminal_id ?? '',
});

// Terminals arrive nested, so no /terminals request is needed.
const processSite = (site) => ({
  id: String(site.id),
  name: site.site_name ?? '',
  code: site.site_id ?? '',
  terminals: (site.terminals ?? []).map(processTerminal),
});

// Offered alongside sites as a "received at" place.
const processBackToBackAccount = (account) => ({
  id: String(account.id),
  name: account.accountName ?? '',
  code: account.accountId ?? '',
});

// A storage unit is a workspace scope only; no form receives at one.
const processStorage = (storage) => ({
  id: String(storage.id),
  name: storage.name ?? '',
  code: storage.storage_id ?? '',
});

const selectedId = (id) => (id == null ? null : String(id));

const processCompanyProfile = (dto) => ({
  companyName: dto?.companyname ?? '',
  companyId: dto?.companyId ?? '',
  addressLine1: dto?.addressLine1 ?? '',
  addressLine2: dto?.addressLine2 ?? '',
  city: dto?.cityName ?? '',
  country: dto?.countryName ?? '',
  zipCode: dto?.zipCode ?? '',
});

export const processUser = (dto) => {
  if (!dto) return null;
  const firstName = dto.firstname ?? '';
  const lastName = dto.lastname ?? '';
  return {
    id: dto.id,
    email: dto.email,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim(),
    role: dto.role ?? null,
    accountName: dto.accountName ?? '',
    companyProfile: processCompanyProfile(dto.account_details),
    sites: (dto.sites ?? []).map(processSite),
    storages: (dto.storages ?? []).map(processStorage),
    backToBackAccounts: (dto.backToBackAccounts ?? []).map(processBackToBackAccount),
    // What the top bar's switcher is on; the server scopes every list to it.
    selection: {
      siteId: selectedId(dto.selected_site_id),
      storageId: selectedId(dto.selected_storage_id),
      backToBackAccountId: selectedId(dto.selected_back_to_back_account_id),
      terminalId: selectedId(dto.selected_terminal_id),
    },
  };
};

export const processInvitationDetails = (dto) => {
  const data = dto.output?.data;
  if (!data) return null;
  return { email: data.email, role: data.role, invitedBy: data.invitedBy };
};

export const buildRegistrationPayload = ({ token, firstName, lastName, password }) => {
  return { token, firstName, lastName, password };
};

export const processRegistrationResult = (dto) => {
  return { user: processUser(dto.output?.data?.user) };
};

export const buildResetPasswordPayload = ({ password }) => {
  return { password };
};

// A 2xx from verifyAuthenticationToken is the whole signal; add fields on demand.
export const processResetTokenResult = () => {
  return { valid: true };
};
